import { FC, useMemo, useRef } from 'react';

import { useFormikContext } from 'formik';
import { camelCase, compact, startCase } from 'lodash';
import { useGetCities, useGetStates } from 'modules/account/settings/hooks';
import { Input } from 'ui/components';
import { Autocomplete, Select, TagsField } from 'ui/components/form';
import FILTER_TYPES from 'ui/components/form/FilterForm/filterTypes';
import { OPTIONS_PROPS } from 'ui/components/nav/Dropdown/Dropdown';
import { SIZE } from 'utils/constants';

import { COMPANY_FORM } from './schema';

const CompanyInfoForm: FC = () => {
  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldValue,
    handleBlur,
    isSubmitting,
  } = useFormikContext<COMPANY_FORM>();

  const { data: statesFetched } = useGetStates({
    countryCode: import.meta.env.VITE_DEFAULT_COUNTRY,
  });

  const { data: citiesFetched } = useGetCities({
    countryCode: import.meta.env.VITE_DEFAULT_COUNTRY,
    isoCode: values.state ?? '',
  });

  const stateOptions = useMemo(
    () =>
      statesFetched?.map((s) => ({
        label: s.name,
        value: s.isoCode,
      })) ?? [],
    [statesFetched],
  );

  const citiesOptions = useMemo(
    () =>
      citiesFetched?.map((s) => ({
        label: s.name,
        value: s.name,
      })) ?? [],
    [citiesFetched],
  );

  const filterIndustry = (inputValue: string) => {
    return FILTER_TYPES.industry.filter((i) =>
      i.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };
  const filterCompanyTypes = (inputValue: string) => {
    return FILTER_TYPES.companyTypes.filter((i) =>
      i.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };
  const filterTimezones = (inputValue: string) => {
    return FILTER_TYPES.timezones.filter((i) =>
      i.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  const loadTimezoneOptions = (
    inputValue: string,
    callback: (options: typeof FILTER_TYPES.industry) => void,
  ) => {
    setTimeout(() => {
      callback(filterTimezones(inputValue));
    }, 1000);
  };
  const loadIndustryOptions = (
    inputValue: string,
    callback: (options: typeof FILTER_TYPES.industry) => void,
  ) => {
    setTimeout(() => {
      callback(filterIndustry(inputValue));
    }, 1000);
  };

  const loadCompanyTypesOptions = (
    inputValue: string,
    callback: (options: typeof FILTER_TYPES.companyTypes) => void,
  ) => {
    setTimeout(() => {
      callback(filterCompanyTypes(inputValue));
    }, 1000);
  };

  const cityRef = useRef<any>(null);

  return (
    <div className="space-y-[24px] pt-[22px]">
      <div className="grid grid-cols-4 gap-x-[34px]">
        <Input
          className="col-span-2 w-full"
          type="text"
          name="name"
          size={SIZE.Medium}
          textType={errors.name && touched.name ? 'danger' : 'default'}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.name}
          placeholder="Company name"
          subText={errors.name && touched.name ? errors.name : ''}
          label="Company name"
          required
          disabled={isSubmitting}
        />
        <Input
          className="col-span-2 w-full"
          type="text"
          name="companyWebsite"
          size={SIZE.Medium}
          textType={
            errors.companyWebsite && touched.companyWebsite
              ? 'danger'
              : 'default'
          }
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.companyWebsite as string}
          placeholder="Company website"
          subText={
            errors.companyWebsite && touched.companyWebsite
              ? errors.companyWebsite
              : ''
          }
          label="Company Website"
          disabled={isSubmitting}
        />
      </div>
      <div className="grid grid-cols-4 gap-x-[34px]">
        <Autocomplete
          className="w-full"
          name="industry"
          size={SIZE.Medium}
          state={errors.industry && touched.industry ? 'danger' : undefined}
          onChange={(val: any) => setFieldValue('industry', val.value)}
          defaultValue={
            values.industry
              ? {
                  label: startCase(camelCase(values.industry)),
                  value: values.industry,
                }
              : undefined
          }
          placeholder="Industry"
          subText={errors.industry && touched.industry ? errors.industry : ''}
          label="Industry"
          loadOptions={loadIndustryOptions}
          isDisabled={isSubmitting}
          isClearable={false}
        />
        <Autocomplete
          className="right w-full"
          name="type"
          size={SIZE.Medium}
          state={errors.type && touched.type ? 'danger' : undefined}
          onChange={(val: any) => {
            setFieldValue('type', val.value ?? '');
          }}
          defaultValue={
            values.type
              ? {
                  label: startCase(camelCase(values.type)),
                  value: values.type,
                }
              : undefined
          }
          placeholder="Type"
          subText={errors.type && touched.type ? errors.type : ''}
          label="Type"
          loadOptions={loadCompanyTypesOptions}
          isDisabled={isSubmitting}
          isClearable={false}
        />
        <Select
          label="State/Region"
          className="w-full"
          selectClassName="rounded"
          value={
            stateOptions?.find(
              (state) => state.value === values.state,
            ) as unknown as string
          }
          onChange={(val: OPTIONS_PROPS) => {
            setFieldValue('state', val.value);
            setFieldValue('city', '');
            cityRef.current?.clearValue();
          }}
          placeholder="Select state"
          disabled={isSubmitting}
          name="state"
          options={stateOptions}
          textType={errors.state ? 'danger' : 'default'}
          subText={errors.state ? errors.state : undefined}
          isSearchable
          size={SIZE.Medium}
        />
        <Select
          ref={cityRef}
          required
          label="City"
          className="w-full"
          selectClassName="rounded"
          value={
            citiesOptions?.find(
              (city) => city.value === values.city,
            ) as unknown as string
          }
          onChange={(val: OPTIONS_PROPS) => {
            setFieldValue('city', val.value);
          }}
          placeholder="Select city"
          disabled={isSubmitting || !values.state}
          name="city"
          options={citiesOptions}
          textType={errors.city ? 'danger' : 'default'}
          subText={errors.city ? errors.city : undefined}
          isSearchable
          size={SIZE.Medium}
        />
      </div>
      <div className="grid grid-cols-4 gap-x-[34px]">
        <Input
          className="w-full"
          type="text"
          name="postalCode"
          size={SIZE.Medium}
          textType={
            errors.postalCode && touched.postalCode ? 'danger' : 'default'
          }
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.postalCode ?? ''}
          placeholder="Postal Code"
          subText={
            errors.postalCode && touched.postalCode ? errors.postalCode : ''
          }
          label="Postal Code"
          disabled={isSubmitting}
        />
        <Input
          className="w-full"
          type="text"
          name="noEmployees"
          size={SIZE.Medium}
          textType={
            errors.noEmployees && touched.noEmployees ? 'danger' : 'default'
          }
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.noEmployees as number}
          placeholder="Number of employees"
          labelSubText=""
          subText={
            errors.noEmployees && touched.noEmployees ? errors.noEmployees : ''
          }
          label="Number of employees"
          disabled={isSubmitting}
        />
        <Input
          className="w-full"
          type="text"
          name="annualRevenue"
          size={SIZE.Medium}
          labelSubText=""
          textType={
            errors.annualRevenue && touched.annualRevenue ? 'danger' : 'default'
          }
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.annualRevenue as number}
          placeholder="Annual Revenue"
          subText={
            errors.annualRevenue && touched.annualRevenue
              ? errors.annualRevenue
              : ''
          }
          label="Annual Revenue"
          disabled={isSubmitting}
        />
        <Autocomplete
          className="right bottom w-full"
          name="timezone"
          size={SIZE.Medium}
          state={errors.timezone && touched.timezone ? 'danger' : undefined}
          onChange={(val: any) => setFieldValue('timezone', val.value)}
          defaultValue={[values?.timezone]}
          placeholder="Timezone"
          subText={errors.timezone && touched.timezone ? errors.timezone : ''}
          label="Timezone"
          loadOptions={loadTimezoneOptions}
          isDisabled={isSubmitting}
        />
      </div>
      <div className="grid grid-cols-2 gap-x-[34px]">
        <Input
          className="w-full"
          type="text"
          name="description"
          size={SIZE.Medium}
          labelSubText=""
          textType={
            errors.description && touched.description ? 'danger' : 'default'
          }
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.description}
          placeholder="Description"
          subText={
            errors.description && touched.description ? errors.description : ''
          }
          label="Description"
          disabled={isSubmitting}
        />
        <Input
          className="w-full"
          type="text"
          name="linkedinUrl"
          size={SIZE.Medium}
          labelSubText=""
          textType={
            errors.linkedinUrl && touched.linkedinUrl ? 'danger' : 'default'
          }
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.linkedinUrl as string}
          placeholder="Linkedin company page"
          subText={
            errors.linkedinUrl && touched.linkedinUrl ? errors.linkedinUrl : ''
          }
          label="Linkedin company page"
          disabled={isSubmitting}
        />
      </div>
      <TagsField
        className="col-span-4 w-full"
        onChange={(val) =>
          setFieldValue(
            'tagId',
            val?.map((tag) => tag.value),
          )
        }
        defaultValue={compact(values.tagId)}
      />
    </div>
  );
};

export default CompanyInfoForm;
